import React, { useState, useEffect } from 'react';
import { Settings, Clock, Lightbulb, UserPlus, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EnhancementSuggestionModal } from './EnhancementSuggestionModal';
import { supabase } from '../../lib/supabase';

interface StatCardsProps {
  patientCount: number;
}

export function StatCards({ patientCount }: StatCardsProps) {
  const navigate = useNavigate();
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [contactRequestCount, setContactRequestCount] = useState<number>(0);
  const [consultationRequestCount, setConsultationRequestCount] = useState<number>(0);

  useEffect(() => {
    const fetchRequestCounts = async () => {
      try {
        // Fetch non-completed contact requests count
        const { data: contactCount, error: contactError } = await supabase.rpc('get_non_completed_contact_requests_count');
        if (contactError) throw contactError;
        setContactRequestCount(contactCount || 0);

        // Fetch non-completed consultation requests count
        const { data: consultationCount, error: consultationError } = await supabase.rpc('get_non_completed_consultation_requests_count');
        if (consultationError) throw consultationError;
        setConsultationRequestCount(consultationCount || 0);
      } catch (error) {
        console.error('Error fetching request counts:', error);
      }
    };

    fetchRequestCounts();
  }, []);

  const totalMessages = contactRequestCount + consultationRequestCount;
  const messageLabel = totalMessages === 1 ? 'Message' : 'Messages';

  return (
    <>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8">
        <div 
          className="bg-white overflow-hidden shadow-lg rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200"
          onClick={() => navigate('/new-patients')}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserPlus className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    New Patients
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    Intake Process
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="bg-white overflow-hidden shadow-lg rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200"
          onClick={() => navigate('/membership-issues')}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Membership Issues
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    View All
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="bg-white overflow-hidden shadow-lg rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200"
          onClick={() => navigate('/settings', { state: { from: 'dashboard' } })}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Settings className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Settings
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    Administration
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="bg-white overflow-hidden shadow-lg rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200"
          onClick={() => setShowSuggestionModal(true)}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Lightbulb className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Future Enhancements
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    Submit Ideas
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div 
          className="bg-white overflow-hidden shadow-lg rounded-lg cursor-pointer hover:bg-gray-50 transition-colors duration-200"
          onClick={() => navigate('/website-messages')}
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MessageSquare className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Website Messages
                  </dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {totalMessages} {messageLabel}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EnhancementSuggestionModal 
        isOpen={showSuggestionModal}
        onClose={() => setShowSuggestionModal(false)}
      />
    </>
  );
}